import * as React from "react";
import Box from "@mui/material/Box";
import LinearProgress, {
  LinearProgressProps,
} from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { adminApiService } from "../../../utils/api";
import { CreateGfFlyerItemFigmaJobDto } from "@swagger/typescript-fetch-goflyer";
import { toastSuccess, toastError } from "../../../utils/pageUtil/toast";
import { MuiConfirmationModal } from "../MuiConfirmationModal";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";

interface LinearDeterminateProps {
  totalCount?: number;
  onComplete?: () => void;
  flyerItemListId?: string;
  figmaJobId?: string;
}

const LinearProgressWithLabel = React.memo(
  ({ variant, value, ...rest }: LinearProgressProps & { value: number }) => {
    const barSx = React.useMemo(
      () => ({
        "& .MuiLinearProgress-bar": {
          backgroundColor: "#ff8038",
        },
      }),
      [],
    );

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ width: "100%", mr: 1 }}>
          <LinearProgress
            variant={variant}
            value={value}
            {...rest}
            sx={barSx}
          />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {`${Math.round(value)}%`}
          </Typography>
        </Box>
      </Box>
    );
  },
);

export default function LinearDeterminate({
  totalCount,
  onComplete,
  flyerItemListId,
  figmaJobId,
}: LinearDeterminateProps) {
  const authHeaders = useAuthHeaders();
  const { t } = useTranslation();
  const [progress, setProgress] = React.useState(0);
  const [progressMessage, setProgressMessage] = React.useState<string>("");
  const [shouldRender, setShouldRender] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = React.useState<boolean>(false);
  const [isCancelling, setIsCancelling] = React.useState<boolean>(false);
  const [currentJobStatus, setCurrentJobStatus] =
    React.useState<CreateGfFlyerItemFigmaJobDto.StatusEnum | null>(null);
  const [currentJobId, setCurrentJobId] = React.useState<string | null>(null);

  const timerRef = React.useRef<NodeJS.Timeout>();
  const jobStartTimeRef = React.useRef<number>(0);
  const totalDurationRef = React.useRef<number>(0);
  const totalItemsRef = React.useRef<number>(0);
  const isCompletedRef = React.useRef<boolean>(false);
  const jobCheckedRef = React.useRef<boolean>(false);
  const progressRef = React.useRef(0);
  const shouldPollAtNinetyFiveRef = React.useRef<boolean>(false);

  const calculateTotalDuration = React.useCallback((totalItems: number) => {
    const baseTimeMs = 1320; // base time per item in milliseconds
    const extraTimeMs = 60000;
    const adjustedTimeMs = baseTimeMs + extraTimeMs / totalItems;
    return totalItems * adjustedTimeMs;
  }, []);

  const cleanupTimers = React.useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const handleCancelClick = React.useCallback(() => {
    setShowCancelModal(true);
  }, []);

  const handleCancelModalClose = React.useCallback(() => {
    setShowCancelModal(false);
  }, []);

  const handleCancelConfirm = React.useCallback(async () => {
    const jobIdToCancel = figmaJobId || currentJobId;
    if (!jobIdToCancel) {
      toastError(t(messages.No_job_ID_available_to_cancel()));
      setShowCancelModal(false);
      return;
    }

    try {
      setIsCancelling(true);
      setShowCancelModal(false);

      const response =
        await adminApiService.gfFlyerItemFigmaJobAdminControllerCancelJob(
          jobIdToCancel,
          authHeaders,
        );

      if (response && "success" in response && !response.success) {
        toastError(
          response.message ||
            t(messages.Failed_to_cancel_job_Please_try_again()),
        );
        setIsCancelling(false);
        return;
      }

      toastSuccess(t(messages.Job_Cancelled_Successfully()));

      // Clean up and reset state
      cleanupTimers();
      isCompletedRef.current = true;
      setShouldRender(false);
      setProgress(0);
      setCurrentJobStatus(null);

      if (onComplete) {
        onComplete();
      }
    } catch (error: any) {
      console.error("Error cancelling job:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        t(messages.Failed_to_cancel_job_Please_try_again());

      toastError(errorMessage);
    } finally {
      setIsCancelling(false);
    }
  }, [figmaJobId, currentJobId, cleanupTimers, onComplete]);

  // Function to check if job is completed - memoized to prevent recreations
  const checkJobCompletion = React.useCallback(async () => {
    if (!flyerItemListId || isCompletedRef.current) return;

    try {
      setIsLoading(true);

      if (figmaJobId) {
        const jobResponse =
          await adminApiService.getOneBaseGfFlyerItemFigmaJobAdminControllerGfFlyerItemFigmaJob(
            figmaJobId,
            undefined,
            ["generatedFlyerImages"],
          );
        setIsLoading(false);

        if (jobResponse) {
          const job = jobResponse;

          if (
            job.status === CreateGfFlyerItemFigmaJobDto.StatusEnum.Completed
          ) {
            setProgress(100);
            isCompletedRef.current = true;
            setCurrentJobStatus(job.status || null);
            cleanupTimers();
            setShouldRender(false);

            toastSuccess(
              t(messages.Flyer_Generated_Successfully_Download_Flyers()),
            );

            if (onComplete) {
              onComplete();
            }
          } else if (
            job.status === CreateGfFlyerItemFigmaJobDto.StatusEnum.InProgress ||
            job.status === CreateGfFlyerItemFigmaJobDto.StatusEnum.Waiting
          ) {
            return;
          } else if (
            job.status === CreateGfFlyerItemFigmaJobDto.StatusEnum.Error
          ) {
            isCompletedRef.current = true;
            cleanupTimers();
            setShouldRender(false);
            toastError(t(messages.Flyer_generation_failed_Please_try_again()));
            toastError(JSON.stringify(job.errorDetails || "Unknown error"));

            if (onComplete) {
              onComplete();
            }
          }
        }
      } else {
        const response =
          await adminApiService.getManyBaseGfFlyerItemFigmaJobAdminControllerGfFlyerItemFigmaJob(
            undefined,
            undefined,
            [
              `flyerItemList.id||eq||${flyerItemListId}`,
              `status||in||${CreateGfFlyerItemFigmaJobDto.StatusEnum.Completed},${CreateGfFlyerItemFigmaJobDto.StatusEnum.InProgress}`,
            ],
            undefined,
            ["updatedAt,DESC"],
            ["generatedFlyerImages"],
            1,
          );
        setIsLoading(false);

        if (response?.data?.[0]) {
          const job = response.data[0];
          setCurrentJobStatus(job.status || null);

          if (
            job.status === CreateGfFlyerItemFigmaJobDto.StatusEnum.Completed
          ) {
            setProgress(100);
            isCompletedRef.current = true;
            cleanupTimers();
            setShouldRender(false);

            if (onComplete) {
              onComplete();
            }
          }
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error checking job completion:", error);
      toastError("Failed to check job status. Please try again.");
    }
  }, [flyerItemListId, onComplete, cleanupTimers, figmaJobId]);

  // Helper function to update the remaining time message - memoized
  const updateRemainingTimeMessage = React.useCallback(
    (remainingMs: number) => {
      const remainingSeconds = Math.max(Math.ceil(remainingMs / 1000), 0);
      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = remainingSeconds % 60;
      const timeDisplay =
        minutes > 0 ? `${minutes} min ${seconds} sec` : `${seconds} seconds`;

      setProgressMessage(
        t(messages.Flyer_Generation_Estimated_time_remaining(), {
          time: timeDisplay,
        }),
      );
    },
    [],
  );

  // Function to start manual progress updates - memoized
  const startManualProgressUpdates = React.useCallback(
    (
      jobStartTime: number,
      calculatedTotalDuration: number,
      totalItems: number,
      completedItems: number,
    ) => {
      cleanupTimers();

      jobStartTimeRef.current = jobStartTime;
      totalDurationRef.current = calculatedTotalDuration;
      totalItemsRef.current = totalItems;
      jobCheckedRef.current = false;

      shouldPollAtNinetyFiveRef.current = !!flyerItemListId;

      // Calculate initial progress
      let initialProgress =
        completedItems > 0
          ? (completedItems / totalItems) * 100
          : ((Date.now() - jobStartTime) / calculatedTotalDuration) * 100;

      initialProgress = Math.min(initialProgress, 99);
      setProgress(initialProgress);
      progressRef.current = initialProgress; // update progressRef

      // Calculate remaining time
      const remainingMs = calculatedTotalDuration * (1 - initialProgress / 100);
      updateRemainingTimeMessage(remainingMs);

      // Use a more optimal interval of 500ms to reduce unnecessary updates
      const updateInterval = 500;

      timerRef.current = setInterval(() => {
        // Calculate progress based on elapsed time
        const elapsed = Date.now() - jobStartTime;
        const progressPercentage = Math.min(
          (elapsed / calculatedTotalDuration) * 100,
          100,
        );

        if (
          shouldPollAtNinetyFiveRef.current &&
          progressPercentage >= 95 &&
          !jobCheckedRef.current
        ) {
          cleanupTimers();

          // Set progress to exactly 95%
          setProgress(95);
          progressRef.current = 95;
          jobCheckedRef.current = true;

          // Update message
          setProgressMessage(
            t(
              messages.Flyer_Generation_Final_stages_verifying_completion_status(),
            ),
          );

          // job status every 30 seconds
          const pollJobStatus = () => {
            checkJobCompletion().then(() => {
              if (!isCompletedRef.current) {
                // If not completed, schedule next check in 30 seconds
                timerRef.current = setTimeout(pollJobStatus, 30000);
              }
            });
          };

          pollJobStatus();
          return;
        }

        // Only update state if there's a significant change (optimization)
        if (Math.abs(progressPercentage - progressRef.current) > 0.5) {
          setProgress(progressPercentage);
          progressRef.current = progressPercentage; // update ref

          // Update remaining time message
          const remainingTime = calculatedTotalDuration - elapsed;
          updateRemainingTimeMessage(Math.max(remainingTime, 0));
        }

        // Check for completion
        if (
          flyerItemListId &&
          !jobCheckedRef.current &&
          progressPercentage >= 100
        ) {
          jobCheckedRef.current = true;
          checkJobCompletion();
        } else if (
          !flyerItemListId &&
          progressPercentage >= 100 &&
          !isCompletedRef.current
        ) {
          setProgress(100);
          progressRef.current = 100;
          isCompletedRef.current = true;
          cleanupTimers();
          if (onComplete) {
            onComplete();
          }
        }
      }, updateInterval);
    },
    [
      flyerItemListId,
      onComplete,
      checkJobCompletion,
      updateRemainingTimeMessage,
      cleanupTimers,
      totalCount,
    ],
  );

  React.useEffect(() => {
    isCompletedRef.current = false;

    setProgress(0);
    setShouldRender(false);
    setCurrentJobStatus(null);
    setCurrentJobId(null);

    // Predefined calculation for direct totalCount
    if (totalCount && totalCount > 0) {
      setShouldRender(true);
      const calculatedTotalDuration = calculateTotalDuration(totalCount);

      // Calculate the estimated time
      const totalSeconds = calculatedTotalDuration / 1000;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = Math.round(totalSeconds % 60);
      const timeDisplay =
        minutes > 0 ? `${minutes} min ${seconds} sec` : `${seconds} seconds`;

      setProgressMessage(
        t(messages.Flyer_Generation_Estimated_time(), { time: timeDisplay }),
      );

      // Set status to InProgress when using totalCount (job is being generated)
      if (figmaJobId) {
        setCurrentJobStatus(CreateGfFlyerItemFigmaJobDto.StatusEnum.InProgress);
        setCurrentJobId(figmaJobId);
      }

      // Start tracking progress
      startManualProgressUpdates(
        Date.now(),
        calculatedTotalDuration,
        totalCount,
        0,
      );
    }
    // Fetch job status for flyerItemListId
    else if (flyerItemListId) {
      setShouldRender(true);
      setIsLoading(true);
      const fetchJobStatus = async () => {
        try {
          setIsLoading(true);
          const response =
            await adminApiService.getManyBaseGfFlyerItemFigmaJobAdminControllerGfFlyerItemFigmaJob(
              undefined,
              undefined,
              [
                `flyerItemList.id||eq||${flyerItemListId}`,
                `status||in||${CreateGfFlyerItemFigmaJobDto.StatusEnum.Waiting},${CreateGfFlyerItemFigmaJobDto.StatusEnum.InProgress}`,
              ],
              undefined,
              ["updatedAt,DESC"],
              ["flyerItemList", "generatedFlyerImages"],
              1,
            );

          if (response?.data?.[0]) {
            const job = response.data[0];
            const totalFlyerItems = job.totalFlyerItemCount || 0;
            setCurrentJobStatus(job.status || null);
            setCurrentJobId(job.id || null);

            if (totalFlyerItems === 0) {
              setShouldRender(false);
              setIsLoading(false);
              return;
            }

            setShouldRender(true);

            if (
              job.status === CreateGfFlyerItemFigmaJobDto.StatusEnum.Waiting
            ) {
              setProgressMessage(t(messages.Server_is_busy_please_wait()));
              setProgress(5);
              setIsLoading(false);
              cleanupTimers();
              timerRef.current = setTimeout(fetchJobStatus, 60000);
            } else if (
              job.status === CreateGfFlyerItemFigmaJobDto.StatusEnum.InProgress
            ) {
              setIsLoading(false);
              const jobStartTime = new Date(job.updatedAt).getTime();
              const calculatedTotalDuration =
                calculateTotalDuration(totalFlyerItems);

              startManualProgressUpdates(
                jobStartTime,
                calculatedTotalDuration,
                totalFlyerItems,
                job.generatedFlyerImages?.length || 0,
              );
            }
          } else {
            setShouldRender(false);
            setIsLoading(false);
          }
        } catch (error) {
          setIsLoading(false);
          setShouldRender(false);
          console.error("Error fetching job status:", error);
        }
      };

      fetchJobStatus();
    }

    // Cleanup on unmount or when dependencies change
    return cleanupTimers;
  }, [totalCount, flyerItemListId, calculateTotalDuration]);

  // Don't render if not needed
  if (!shouldRender) return null;

  // Show cancel button only for Waiting or InProgress status
  const canCancel =
    (figmaJobId || currentJobId) &&
    (currentJobStatus === CreateGfFlyerItemFigmaJobDto.StatusEnum.Waiting ||
      currentJobStatus === CreateGfFlyerItemFigmaJobDto.StatusEnum.InProgress);

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            mb: -0.5,
          }}
        >
          <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>
            {isLoading ? t(messages.Loading_job_status()) : progressMessage}
          </Typography>
          {canCancel && !isCancelling && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleCancelClick}
              sx={{ ml: 2, minWidth: "80px", height: "24px", fontSize: "12px" }}
            >
              {t(messages.Cancel())}
            </Button>
          )}
          {isCancelling && (
            <Typography
              sx={{ fontWeight: "bold", fontSize: "12px", color: "error.main" }}
            >
              {t(messages.Cancelling())}
            </Typography>
          )}
        </Box>
        <LinearProgressWithLabel
          value={isLoading ? 10 : progress}
          variant={isLoading ? "indeterminate" : "determinate"}
        />
      </Box>

      <MuiConfirmationModal
        open={showCancelModal}
        handleCancel={handleCancelModalClose}
        handleConfirm={handleCancelConfirm}
        title={t(messages.Cancel_Job_Title())}
        description={t(messages.Cancel_Job_Description())}
        confirmButtonColor="#d32f2f"
        warningMessage={t(messages.Cancel_Job_WarningMessage())}
      />
    </>
  );
}
